/**
 * review-form.js - the native Write a Review form.
 *
 * Progressive enhancement, not a dependency. snippets/review-form.liquid is a
 * real <form> with a real action, so with this file missing or broken the
 * button still navigates to a working page and the form still posts. What the
 * script adds is the dialog, the photo picker, inline validation and a result
 * the customer sees without leaving the product page.
 *
 * The backend is the authority on every rule enforced here. Client-side checks
 * exist to save a round trip and to put a message next to the field that
 * caused it, never to decide whether a review is acceptable.
 */

(() => {
  'use strict';

  const TIMEOUT_MS = 60000;      // five 12 MB photos on a poor connection
  // The server discards a submission completed in under three seconds as
  // automated, and answers it with a success the customer cannot tell apart
  // from a real one. A person who autofills and submits immediately would
  // lose their review to that rule without ever knowing, so the client holds
  // the request back until the window has passed. Nothing is weakened: a bot
  // that skips this script is unaffected either way.
  const MIN_FORM_MS = 3000;
  const BODY_MIN = 10;
  const BODY_MAX = 5000;
  const NAME_MAX = 80;
  const EMAIL_MAX = 254;

  /** Matches lib/validate.js: reject obvious nonsense, not valid addresses. */
  const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/u;

  /**
   * Statuses whose server message is written for the customer and safe to
   * show verbatim. Everything else gets local copy, so a stack trace, a
   * database error or a provider response can never reach the page.
   */
  const SAFE_MESSAGE_STATUSES = new Set([409, 413, 422, 429]);

  const PHOTO_TYPES = /^image\/(jpeg|png|webp|heic|heif|avif)$/i;
  const VIDEO_EXT = /\.(mp4|mov|m4v|webm)$/i;

  const COPY = {
    rating: 'Please choose a rating.',
    title: 'Please add a title for your review.',
    titleLong: 'Please keep your title to 120 characters or fewer.',
    bodyShort: `Please write at least ${BODY_MIN} characters.`,
    bodyLong: `Please keep your review to ${BODY_MAX} characters or fewer.`,
    name: 'Please add the name to show on your review.',
    nameLong: `Please keep your name to ${NAME_MAX} characters or fewer.`,
    email: 'Please check your email address.',
    tooManyPhotos: (max) => `Please attach no more than ${max} photos.`,
    oneVideo: 'One video per review. Remove the one you have added to choose another.',
    videoTooBig: (name, mb) => `${name} is larger than ${mb} MB.`,
    videoTooLong: (secs) => `Please keep videos to ${secs} seconds or less.`,
    wrongType: (name) => `${name} is not a photo we can accept. Use JPEG, PNG, WebP, HEIC or AVIF.`,
    photoTooBig: (name, mb) => `${name} is larger than ${mb} MB.`,
    unavailable: 'Something went wrong at our end. Please try again in a moment.',
    offline: 'We could not reach the review service. Please check your connection and try again.',
    timeout: 'That took too long to send. Please try again, or with fewer photos.',
  };

  const FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const bytesToMb = (n) => Math.round(n / 1048576);

  // ── Modal ────────────────────────────────────────────────────────────────

  /**
   * Follows the theme's modal conventions - the [open] attribute, the
   * overflow-hidden body class, a ModalClose- button, role="dialog" on the
   * panel - and adds the two things assets/modal-dialog.js does not do: a
   * focus trap, and returning focus to whatever opened the dialog.
   */
  class ReviewFormModal extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.panel = this.querySelector('[role="dialog"]');
      this.openedBy = null;

      this.addEventListener('click', (e) => {
        if (e.target === this) this.hide();
        if (e.target.closest('[data-review-close], [data-success-close]')) this.hide();
      });

      this._onKeydown = (e) => {
        if (!this.hasAttribute('open')) return;
        if (e.key === 'Escape') {
          // Stopped in the capture phase because the theme also listens for
          // Escape on the document, to close its drawers, and that handler
          // moves focus into the header. While this dialog is open, Escape
          // belongs to it. Anything not Escape is left to propagate.
          e.preventDefault();
          e.stopPropagation();
          this._swallowEscapeKeyup = true;
          this.hide();
          return;
        }
        if (e.key === 'Tab') this.trapFocus(e);
      };

      // PanelClose in app.js closes the theme's side panels on Escape KEYUP,
      // and in doing so focuses the cart toggle whether or not a panel was
      // open. That fires after this dialog has already closed, so it would
      // take the focus we just handed back to the trigger. The keyup that
      // belongs to our own Escape is swallowed; every other keyup passes.
      this._onKeyup = (e) => {
        if (!this._swallowEscapeKeyup) return;
        this._swallowEscapeKeyup = false;
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        if (this.openedBy && document.contains(this.openedBy)) {
          this.openedBy.focus({ preventScroll: true });
        }
      };

      // On the document, not the element: Escape has to work even when focus
      // has slipped outside the panel.
      document.addEventListener('keydown', this._onKeydown, true);
      document.addEventListener('keyup', this._onKeyup, true);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeydown, true);
      document.removeEventListener('keyup', this._onKeyup, true);
      document.body.classList.remove('overflow-hidden');
    }

    trapFocus(e) {
      const items = [...this.panel.querySelectorAll(FOCUSABLE)]
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    show(opener) {
      this.openedBy = opener || document.activeElement;
      // Remembered so the page returns to where it was: iOS reports a locked
      // body as scrolled to the top otherwise.
      this._scrollY = window.scrollY;
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      // Read a layout property to force the style recalculation through before
      // focusing. The dialog is visibility:hidden until [open] lands, and
      // focus() on a hidden element is silently ignored, which is what a
      // requestAnimationFrame here did instead: rAF runs before style is
      // recomputed for the frame.
      void this.panel.offsetHeight;
      const target = this.querySelector('[data-success].is-shown')
        || this.querySelector('input[name="rating"]')
        || this.panel;
      target.focus({ preventScroll: true });
    }

    hide() {
      if (!this.hasAttribute('open')) return;
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');
      if (this.openedBy && document.contains(this.openedBy)) {
        this.openedBy.focus({ preventScroll: true });
      }
      if (typeof this._scrollY === 'number') window.scrollTo(0, this._scrollY);
    }
  }

  if (!customElements.get('review-form-modal')) {
    customElements.define('review-form-modal', ReviewFormModal);
  }

  // ── Trigger ──────────────────────────────────────────────────────────────

  // Delegated so a section re-render in the theme editor needs no rebinding.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-review-open]');
    if (!trigger) return;
    const modal = document.getElementById(trigger.getAttribute('data-review-open'));
    if (!modal || typeof modal.show !== 'function') return;   // let the link navigate
    e.preventDefault();
    modal.show(trigger);
  });

  // ── Form ─────────────────────────────────────────────────────────────────

  class ReviewForm {
    constructor(form) {
      this.form = form;
      this.endpoint = form.getAttribute('action');
      this.maxFiles = Number(form.dataset.maxFiles) || 5;
      this.maxBytes = Number(form.dataset.maxBytes) || 12 * 1048576;
      this.busy = false;
      this.files = [];
      this.video = null;
      this.previewUrls = [];

      this.submitButton = form.querySelector('[data-submit]');
      this.status = form.querySelector('[data-form-status]');
      this.success = form.parentElement.querySelector('[data-success]');
      this.photoInput = form.querySelector('[data-photo-input]');
      this.videoInput = form.querySelector('[data-video-input]');
      this.videoPreview = form.querySelector('[data-video-preview]');
      this.maxVideoBytes = Number(form.dataset.maxVideoBytes) || 200 * 1024 * 1024;
      this.maxVideoSecs = Number(form.dataset.maxVideoSecs) || 60;
      this.photoPick = form.querySelector('[data-photo-pick]');
      this.previews = form.querySelector('[data-photo-previews]');
      this.slots = form.querySelector('[data-photo-slots]');
      this.bodyField = form.querySelector('textarea[name="body"]');
      this.bodyCount = form.querySelector('[data-body-count]');

      // Rebuilding a FileList needs DataTransfer. Without it the customer can
      // still attach photos, they just cannot remove one individually.
      this.canEditFiles = typeof DataTransfer === 'function' && (() => {
        try { return new DataTransfer().items != null; } catch { return false; }
      })();

      // Started at page load rather than at modal open: the server rejects a
      // form completed in under three seconds, and the earlier clock is the
      // one that is kinder to a fast human without helping a bot, which can
      // wait either way.
      const ts = form.querySelector('[data-form-ts]');
      if (ts) ts.value = String(Date.now());

      form.addEventListener('submit', (e) => this.onSubmit(e));
      if (this.photoInput) this.photoInput.addEventListener('change', (e) => this.onPick(e));
      if (this.videoInput) {
        this.videoInput.addEventListener('change', () => {
          const picked = [...(this.videoInput.files || [])];
          // Clear BEFORE accepting, not after. The input keeps a rejected
          // file, so picking the same one again would fire no change event
          // and look like nothing happened. Clearing afterwards instead wiped
          // what syncVideoInput had just put there, and the video was then
          // left out of the submission entirely.
          if (this.canEditFiles) this.videoInput.value = '';
          this.acceptFiles(picked);
        });
      }

      // Drag and drop over the whole upload area. dragover must be cancelled
      // or the browser navigates away to the dropped file.
      this.drop = form.querySelector('[data-dropzone]');
      if (this.drop) {
        let depth = 0;   // dragenter/leave fire per child, so count them
        const on = (v) => this.drop.classList.toggle('is-over', v);
        this.drop.addEventListener('dragenter', (e) => { e.preventDefault(); depth++; on(true); });
        this.drop.addEventListener('dragover', (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; });
        this.drop.addEventListener('dragleave', () => { if (--depth <= 0) { depth = 0; on(false); } });
        this.drop.addEventListener('drop', (e) => {
          e.preventDefault(); depth = 0; on(false);
          this.acceptFiles([...(e.dataTransfer?.files || [])]);
        });
      }
      if (this.bodyField) this.bodyField.addEventListener('input', () => this.onBodyInput());
      // Clear a field's error as soon as the customer works on it again.
      form.addEventListener('input', (e) => {
        const named = e.target.name;
        if (named) this.clearError(named);
      });
      form.addEventListener('change', (e) => {
        if (e.target.name === 'rating') this.clearError('rating');
      });

      this.updateSlots();
    }

    // ── errors ────────────────────────────────────────────────────────────

    errorNode(field) {
      return this.form.querySelector(`[data-error-for="${field}"]`);
    }

    showError(field, message) {
      const node = this.errorNode(field);
      if (node) {
        node.textContent = message;
        node.classList.add('is-shown');
      }
      for (const input of this.form.querySelectorAll(`[name="${field}"]`)) {
        input.classList.add('is-invalid');
        input.setAttribute('aria-invalid', 'true');
      }
    }

    clearError(field) {
      const node = this.errorNode(field);
      if (node) { node.textContent = ''; node.classList.remove('is-shown'); }
      for (const input of this.form.querySelectorAll(`[name="${field}"]`)) {
        input.classList.remove('is-invalid');
        input.removeAttribute('aria-invalid');
      }
    }

    clearAllErrors() {
      for (const node of this.form.querySelectorAll('[data-error-for]')) {
        this.clearError(node.getAttribute('data-error-for'));
      }
      this.setStatus('');
    }

    setStatus(message) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.classList.toggle('is-shown', Boolean(message));
      this.status.classList.toggle('review-form__status--error', Boolean(message));
    }

    // ── body counter ──────────────────────────────────────────────────────

    onBodyInput() {
      if (this.bodyCount) this.bodyCount.textContent = String(this.bodyField.value.length);
    }

    // ── photos ────────────────────────────────────────────────────────────

    onPick(event) {
      this.acceptFiles([...(event.target.files || [])]);
    }

    /**
     * One intake for both the picker and the drop zone, so a dragged file is
     * held to exactly the same rules as a chosen one.
     */
    acceptFiles(picked) {
      this.clearError('photos');
      if (!picked.length) return;

      // A file is routed by what it is, not by which control it arrived
      // through: dropping a video onto the area is the same as choosing it.
      // Some Android browsers send an empty type for a .mov, so the extension
      // is a fallback rather than the rule.
      const isVideo = (f) => /^video\//.test(f.type) || (!f.type && VIDEO_EXT.test(f.name || ''));
      const videos = picked.filter(isVideo);
      picked = picked.filter((f) => !isVideo(f));
      const wrongType = picked.filter((f) => f.type && !PHOTO_TYPES.test(f.type));
      picked = picked.filter((f) => !f.type || PHOTO_TYPES.test(f.type));

      let videoProblem = null;
      if (videos.length) {
        this.clearError('video');
        if (this.video || videos.length > 1) videoProblem = COPY.oneVideo;
        else if (videos[0].size > this.maxVideoBytes) {
          videoProblem = COPY.videoTooBig(videos[0].name, Math.round(this.maxVideoBytes / 1048576));
        } else {
          this.setVideo(videos[0]);
        }
      }

      const oversize = [];
      let overflowed = false;

      for (const file of picked) {
        if (this.files.length >= this.maxFiles) { overflowed = true; break; }
        if (file.size > this.maxBytes) { oversize.push(file); continue; }
        // Same file picked twice in two goes is a mistake, not an intent.
        const already = this.files.some(
          (f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified
        );
        if (!already) this.files.push(file);
      }

      if (videoProblem) {
        this.showError('video', videoProblem);
      }
      if (wrongType.length) {
        this.showError('photos', COPY.wrongType(wrongType[0].name));
      } else if (oversize.length) {
        this.showError('photos', COPY.photoTooBig(oversize[0].name, bytesToMb(this.maxBytes)));
      } else if (overflowed) {
        this.showError('photos', COPY.tooManyPhotos(this.maxFiles));
      }

      this.syncFileInput();
      this.renderPreviews();
      this.updateSlots();
    }

    /**
     * Hold the chosen video and show it back.
     *
     * The duration is checked here when the browser can read it, which saves
     * the customer a 200 MB upload that the server would only refuse. But a
     * browser that cannot decode the codec reports nothing, and an iPhone
     * HEVC .mov in Chrome is exactly that case, so an unreadable duration is
     * not treated as a failure. The server probes properly either way.
     */
    setVideo(file) {
      this.video = file;
      this.syncVideoInput();
      this.renderVideo();

      if (!('createObjectURL' in URL)) return;
      const url = URL.createObjectURL(file);
      const probe = document.createElement('video');
      probe.preload = 'metadata';
      probe.muted = true;
      const done = () => { URL.revokeObjectURL(url); };
      probe.addEventListener('loadedmetadata', () => {
        const secs = Number(probe.duration);
        if (Number.isFinite(secs) && secs > 0 && secs > this.maxVideoSecs + 0.5) {
          this.removeVideo();
          this.showError('video', COPY.videoTooLong(this.maxVideoSecs));
        } else if (Number.isFinite(secs) && secs > 0) {
          this.renderVideo({ seconds: secs, playable: true });
        }
        done();
      });
      // Cannot decode it here: accept it anyway and let the server decide.
      probe.addEventListener('error', done);
      probe.src = url;
    }

    removeVideo() {
      this.video = null;
      this.syncVideoInput();
      this.renderVideo();
      this.clearError('video');
    }

    syncVideoInput() {
      if (!this.videoInput) return;
      if (!this.canEditFiles) return;      // no DataTransfer: the input stands as picked
      const dt = new DataTransfer();
      if (this.video) dt.items.add(this.video);
      this.videoInput.files = dt.files;
    }

    /**
     * A tile either way. When the browser can play the file it gets a real
     * preview; when it cannot, which is the normal iPhone-in-Chrome case, it
     * gets a named placeholder rather than an error.
     */
    renderVideo({ seconds = null, playable = false } = {}) {
      const wrap = this.videoPreview;
      if (!wrap) return;
      if (!this.video) {
        wrap.hidden = true;
        wrap.innerHTML = '';
        return;
      }
      const mb = (this.video.size / 1048576).toFixed(1);
      const len = seconds ? `${Math.round(seconds)}s · ` : '';
      const name = this.video.name || 'video';
      wrap.hidden = false;
      wrap.innerHTML = `
        <div class="review-form__vtile">
          ${playable
            ? '<video class="review-form__vthumb" muted playsinline preload="metadata"></video>'
            : `<div class="review-form__vthumb review-form__vthumb--generic" aria-hidden="true">
                 <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                   <rect x="2.5" y="6" width="13" height="12" rx="2.5" stroke="currentColor" stroke-width="2"/>
                   <path d="M16.5 10.5l5-3v9l-5-3v-3z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
                 </svg>
               </div>`}
          <div class="review-form__vmeta">
            <span class="review-form__vname"></span>
            <span class="review-form__vsize">${len}${mb} MB</span>
          </div>
          <button type="button" class="review-form__vremove" data-video-remove
                  aria-label="Remove video">&times;</button>
        </div>`;
      // textContent, never innerHTML: the filename is the customer's.
      wrap.querySelector('.review-form__vname').textContent = name;
      if (playable) {
        const el = wrap.querySelector('video');
        const url = URL.createObjectURL(this.video);
        el.src = url;
        el.addEventListener('loadeddata', () => URL.revokeObjectURL(url), { once: true });
      }
      wrap.querySelector('[data-video-remove]').addEventListener('click', () => this.removeVideo());
    }

    /**
     * Without DataTransfer the native FileList stands as picked, so the count
     * has to follow the input rather than our own array.
     */
    syncFileInput() {
      if (!this.photoInput) return;
      if (!this.canEditFiles) {
        this.files = [...(this.photoInput.files || [])].slice(0, this.maxFiles);
        return;
      }
      const dt = new DataTransfer();
      for (const file of this.files) dt.items.add(file);
      this.photoInput.files = dt.files;
    }

    removeFile(index) {
      this.files.splice(index, 1);
      this.clearError('photos');
      this.syncFileInput();
      this.renderPreviews();
      this.updateSlots();
      const next = this.previews.querySelector('.review-form__thumb-remove');
      (next || this.photoPick).focus();
    }

    renderPreviews() {
      if (!this.previews) return;
      for (const url of this.previewUrls) URL.revokeObjectURL(url);
      this.previewUrls = [];
      this.previews.textContent = '';

      this.files.forEach((file, index) => {
        const item = document.createElement('li');
        item.className = 'review-form__thumb';

        const url = URL.createObjectURL(file);
        this.previewUrls.push(url);

        const img = document.createElement('img');
        img.src = url;
        img.alt = '';
        // HEIC has no preview outside Safari. The file is valid and the server
        // converts it, so a failed preview must never remove the attachment.
        img.addEventListener('error', () => {
          const generic = document.createElement('span');
          generic.className = 'review-form__thumb-generic';
          generic.innerHTML = '<strong>Photo</strong>';
          const name = document.createElement('span');
          name.textContent = file.name.split('.').pop().toUpperCase();
          generic.appendChild(name);
          img.replaceWith(generic);
        });
        item.appendChild(img);

        if (this.canEditFiles) {
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'review-form__thumb-remove';
          remove.innerHTML = '&times;';
          remove.setAttribute('aria-label', `Remove ${file.name}`);
          remove.addEventListener('click', () => this.removeFile(index));
          item.appendChild(remove);
        }

        this.previews.appendChild(item);
      });
    }

    updateSlots() {
      const used = this.files.length;
      const full = used >= this.maxFiles;
      if (this.slots) this.slots.textContent = `${used} of ${this.maxFiles} added`;
      if (this.photoPick) {
        this.photoPick.classList.toggle('is-full', full);
        this.photoPick.setAttribute('aria-disabled', full ? 'true' : 'false');
      }
      // The input is deliberately left alone when full: it is neither disabled
      // nor marked aria-disabled. A disabled control is left out of FormData
      // entirely, so disabling it at the fifth photo silently dropped all five
      // on submit. Fullness is carried by the label, which is the control the
      // customer actually sees, and onPick refuses anything over the limit.
    }

    // ── validation ────────────────────────────────────────────────────────

    /** @returns {string|null} the name of the first field that failed. */
    validate() {
      this.clearAllErrors();
      let first = null;
      const fail = (field, message) => {
        this.showError(field, message);
        if (!first) first = field;
      };

      if (!this.form.querySelector('input[name="rating"]:checked')) fail('rating', COPY.rating);

      const title = (this.form.querySelector('[name="title"]')?.value || '').trim();
      if (title.length < 2) fail('title', COPY.title);
      else if (title.length > 120) fail('title', COPY.titleLong);

      const body = (this.bodyField?.value || '').trim();
      if (body.length < BODY_MIN) fail('body', COPY.bodyShort);
      else if (body.length > BODY_MAX) fail('body', COPY.bodyLong);

      const name = (this.form.querySelector('[name="name"]')?.value || '').trim();
      if (!name) fail('name', COPY.name);
      else if (name.length > NAME_MAX) fail('name', COPY.nameLong);

      const email = (this.form.querySelector('[name="email"]')?.value || '').trim();
      if (!email || email.length > EMAIL_MAX || !EMAIL_RE.test(email)) fail('email', COPY.email);

      if (this.video && this.video.size > this.maxVideoBytes) {
        fail('video', COPY.videoTooBig(this.video.name, Math.round(this.maxVideoBytes / 1048576)));
      }

      if (this.files.length > this.maxFiles) fail('photos', COPY.tooManyPhotos(this.maxFiles));
      const big = this.files.find((f) => f.size > this.maxBytes);
      if (big) fail('photos', COPY.photoTooBig(big.name, bytesToMb(this.maxBytes)));

      return first;
    }

    focusField(field) {
      const target = this.form.querySelector(`[name="${field}"]`);
      if (target) target.focus({ preventScroll: false });
    }

    // ── submit ────────────────────────────────────────────────────────────

    setBusy(busy) {
      this.busy = busy;
      this.form.classList.toggle('is-busy', busy);
      if (this.submitButton) {
        this.submitButton.disabled = busy;
        this.submitButton.setAttribute('aria-busy', busy ? 'true' : 'false');
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      // The guard is for the customer's sake, not for correctness: the server
      // fingerprints every submission, so a double tap that gets past this
      // returns the review that already exists rather than creating a second.
      if (this.busy) return;

      const invalid = this.validate();
      if (invalid) { this.focusField(invalid); return; }

      this.setBusy(true);

      const startedAt = Number(this.form.querySelector('[data-form-ts]')?.value);
      const tooFast = Number.isFinite(startedAt) ? MIN_FORM_MS - (Date.now() - startedAt) : 0;
      if (tooFast > 0) await new Promise((r) => setTimeout(r, tooFast));

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

      try {
        const body = new FormData(this.form);
        // An untouched file input still serialises as an empty part. The
        // server now ignores those, but sending one is pointless and it is
        // one more thing to go wrong, so a review with no photos carries no
        // photo field at all.
        if (!this.files.length) body.delete(this.photoInput?.name || 'photos');
        if (!this.video) body.delete(this.videoInput?.name || 'video');

        const response = await fetch(this.endpoint, {
          method: 'POST',
          // No custom headers beyond the CORS safelist, so the browser sends
          // this without a preflight.
          headers: { Accept: 'application/json' },
          body,
          signal: controller.signal,
        });

        let payload = {};
        try { payload = await response.json(); } catch { /* not every error has a body */ }
        this.handle(response.status, payload);
      } catch (error) {
        this.setStatus(error && error.name === 'AbortError' ? COPY.timeout : COPY.offline);
      } finally {
        clearTimeout(timer);
        this.setBusy(false);
      }
    }

    handle(status, payload) {
      // 202 is the answer to a request the server judged automated. It looks
      // exactly like success on purpose, and the form must not betray that,
      // so it is handled in the same branch rather than a special case.
      if (status === 201 || status === 200 || status === 202) return this.onSuccess();

      const serverMessage = SAFE_MESSAGE_STATUSES.has(status) && typeof payload.message === 'string'
        ? payload.message
        : null;

      if (status === 422) {
        const field = payload.field;
        if (field && this.errorNode(field)) {
          this.showError(field, serverMessage || COPY.unavailable);
          this.focusField(field);
        } else {
          this.setStatus(serverMessage || COPY.unavailable);
        }
        return;
      }

      if (status === 413) {
        this.showError('photos', serverMessage || COPY.photoTooBig('That photo', bytesToMb(this.maxBytes)));
        return;
      }

      // 409 recent duplicate, 429 rate limited: both are about the person
      // rather than a field, so they belong above the button.
      this.setStatus(serverMessage || COPY.unavailable);
    }

    onSuccess() {
      for (const url of this.previewUrls) URL.revokeObjectURL(url);
      this.previewUrls = [];
      this.form.hidden = true;
      if (this.success) {
        this.success.classList.add('is-shown');
        // role="status" on the panel means the screen reader reads it; moving
        // focus there means a keyboard user lands on it too.
        this.success.focus({ preventScroll: true });
        this.success.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }

  // ── Boot ─────────────────────────────────────────────────────────────────

  function init(root = document) {
    for (const form of root.querySelectorAll('[data-review-form]')) {
      if (form.dataset.reviewReady) continue;
      form.dataset.reviewReady = '1';
      new ReviewForm(form);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor: a section re-render replaces the markup wholesale.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
